// WindowsDialog.tsx — Up to 6 standard windows (wdvar)

import DialogBase from './DialogBase';
import { SlotEditor } from './SlotEditor';
import { usePendingSlot } from './usePendingSlot';
import { SizePalette, WINDOW_PRESETS } from '../utils/sizePalette';
import type { SizePreset } from '../utils/sizePalette';
import { useShallowStore } from '../store/useStore';
import type { WdData } from '../types/panelvar';

const FIELDS = [
  { key: 'mark',   label: 'Mark',    type: 'text'   as const },
  { key: 'width',  label: 'Width',   type: 'number' as const, unit: '"', min: 0 },
  { key: 'height', label: 'Height',  type: 'number' as const, unit: '"', min: 0 },
  { key: 'x',      label: 'X Ctr',   type: 'number' as const, unit: '"', min: 0 },
  { key: 'y',      label: 'Y Ctr',   type: 'number' as const, unit: '"', min: 0 },
  { key: 'sill',   label: 'Sill Ht', type: 'number' as const, unit: '"', min: 0 },
];

export default function WindowsDialog() {
  const { ui, project, updatePanel } = useShallowStore(s => ({
    ui: s.ui, project: s.project, updatePanel: s.updatePanel,
  }));
  const panel = project?.panels.find(p => p.id === ui.activePanelId);
  const [slots, setSlots] = usePendingSlot<WdData>('wdvar', panel?.wdvar ?? [],
    (px, py, mark) => ({ mark, x: px, y: py, sill: Math.max(0, py - 24), sillHeight: Math.max(0, py - 24), headHeight: py + 24 }),
    { mark: '', type: 'fixed' as const, x: 0, y: 0, width: 36, height: 48, sillHeight: 0, sill: 0, headHeight: 0, jamb: 0 });
  if (!panel) return null;

  const panelW = panel.mpvar.width  || 360;
  const panelH = panel.mpvar.height || 336;
  const ffeY   = -(panel.mpvar.botElev ?? 0) * 12;

  function addFromPalette(p: SizePreset) {
    const cy = panelH / 2 - ffeY;
    const data: WdData = {
      mark: '', type: 'fixed',
      x: panelW / 2, y: cy,
      width: p.width, height: p.height,
      sillHeight: cy - p.height / 2,
      sill:       cy - p.height / 2,
      headHeight: cy + p.height / 2,
      jamb: 0,
    };
    setSlots(prev => {
      const idx = prev.findIndex(s => !s.enabled);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { enabled: true, data };
        return next;
      }
      if (prev.length < 6) return [...prev, { enabled: true, data }];
      return prev;
    });
  }

  return (
    <DialogBase title="Windows (6 max)" onOk={() => updatePanel(panel.id, { wdvar: slots })}>
      <SizePalette title="Common Window Sizes" presets={WINDOW_PRESETS} onSelect={addFromPalette} />
      <SlotEditor
        slots={slots}
        fields={FIELDS}
        maxSlots={6}
        onChange={v => setSlots(v)}
        defaultData={() => ({ mark: '', type: 'fixed' as const, x: panelW / 2, y: panelH / 2 - ffeY, width: 36, height: 48, sillHeight: panelH / 2 - ffeY - 24, sill: panelH / 2 - ffeY - 24, headHeight: panelH / 2 - ffeY + 24, jamb: 0 })}
        label="Win"
      />
    </DialogBase>
  );
}
